// HLZ (Helicopter Landing Zone) — vurderer om en landingsplass er egnet for en
// gitt helikopterklasse: størrelse (touchdown + rotorklaring), terrenghelning,
// underlag og hindringer i inn-/utflygingskorridoren (glidetallskrav). Anbefaler
// innflyging opp mot vinden. Doktrine-forenklet (ATP-nivå), ugradert, testbart.
import { z } from 'zod';
export const HeloClass = z.enum(['light', 'medium', 'heavy']);
export const HELO_CLASS_LABEL = {
    light: 'Lett (f.eks. Bell 412 / H145)',
    medium: 'Middels (f.eks. NH90 / Black Hawk)',
    heavy: 'Tung (f.eks. Chinook / AW101)',
};
export const HELO_SPEC = {
    light: { minLandingDiameterM: 25, maxSlopeDeg: 15, approachGlideRatio: 8 },
    medium: { minLandingDiameterM: 35, maxSlopeDeg: 12, approachGlideRatio: 10 },
    heavy: { minLandingDiameterM: 50, maxSlopeDeg: 7, approachGlideRatio: 10 },
};
export const HlzSurface = z.enum(['firm', 'soft', 'snow', 'sand', 'water', 'unknown']);
/** Underlag som gir advarsel (brownout/whiteout/synkefare). */
const RISKY_SURFACE = {
    firm: null,
    soft: 'Mykt underlag — synkefare/velt',
    snow: 'Snø — whiteout ved landing',
    sand: 'Sand/støv — brownout ved landing',
    water: 'Vann — ikke egnet for landing',
    unknown: 'Ukjent underlag — rekognoser',
};
export const HLZ_SUITABILITY_LABEL = {
    adequate: 'Egnet', marginal: 'Marginal', inadequate: 'Uegnet',
};
const norm360 = (d) => ((d % 360) + 360) % 360;
/** Minste vinkeldifferanse mellom to peilinger (0..180). */
function angDiff(a, b) {
    const d = Math.abs(norm360(a) - norm360(b));
    return d > 180 ? 360 - d : d;
}
/**
 * Vurder en HLZ. «Uegnet» ved for liten flate, for bratt, vann, eller hinder som
 * bryter glidetallet i inn-/utflygingskorridoren. «Marginal» ved risiko-underlag
 * eller liten margin. Ellers «egnet». Innflyging anbefales opp mot vinden.
 */
export function assessHlz(input) {
    const spec = HELO_SPEC[input.heloClass];
    const reasons = [];
    let suitability = 'adequate';
    const demote = (to) => {
        const rank = { adequate: 0, marginal: 1, inadequate: 2 };
        if (rank[to] > rank[suitability])
            suitability = to;
    };
    // Størrelse.
    if (input.usableDiameterM < spec.minLandingDiameterM) {
        reasons.push(`For liten: ${input.usableDiameterM} m < krav ${spec.minLandingDiameterM} m`);
        demote('inadequate');
    }
    else if (input.usableDiameterM < spec.minLandingDiameterM * 1.2) {
        reasons.push('Liten margin på størrelse — presis landing kreves');
        demote('marginal');
    }
    // Helning.
    if (input.slopeDeg > spec.maxSlopeDeg) {
        reasons.push(`For bratt: ${input.slopeDeg}° > maks ${spec.maxSlopeDeg}°`);
        demote('inadequate');
    }
    else if (input.slopeDeg > spec.maxSlopeDeg * 0.75) {
        reasons.push('Bratt helning nær grensen');
        demote('marginal');
    }
    // Underlag.
    const surface = input.surface ?? 'unknown';
    const surfWarn = RISKY_SURFACE[surface];
    if (surface === 'water') {
        reasons.push(surfWarn);
        demote('inadequate');
    }
    else if (surfWarn) {
        reasons.push(surfWarn);
        demote('marginal');
    }
    // Hindringer i korridoren: bryter hinderet glidetallet? (avstand/høyde < krav).
    const radius = input.usableDiameterM / 2;
    const blocking = [];
    for (const o of input.obstacles ?? []) {
        if (o.heightM <= 0)
            continue;
        const ratio = (o.distanceM + radius) / o.heightM;
        if (ratio < spec.approachGlideRatio)
            blocking.push(o);
    }
    if (blocking.length) {
        reasons.push(`${blocking.length} hinder bryter glidetall ${spec.approachGlideRatio}:1`);
        demote('marginal');
        // Er HELE korridoren blokkert (hinder i alle kvadranter)? → uegnet.
        const quadrants = new Set(blocking.map((o) => Math.floor(norm360(o.bearingDeg) / 90)));
        if (quadrants.size >= 4) {
            reasons.push('Hinder i alle retninger — ingen trygg korridor');
            demote('inadequate');
        }
    }
    // Anbefalt innflyging: opp mot vinden, vekk fra verste hinder om mulig.
    let recommendedApproachDeg;
    if (input.windFromDeg !== undefined) {
        recommendedApproachDeg = norm360(input.windFromDeg); // fly INN mot vinden = mot der den kommer fra
        // Hvis et blokkerende hinder ligger i innflygingsretningen, noter det.
        if (blocking.some((o) => angDiff(o.bearingDeg, recommendedApproachDeg) < 30)) {
            reasons.push('Hinder i anbefalt (motvinds) innflyging — vurder skrå innflyging');
        }
    }
    if (!reasons.length)
        reasons.push('Ingen begrensninger identifisert');
    return { suitability, reasons, recommendedApproachDeg, blockingObstacles: blocking, spec };
}
//# sourceMappingURL=hlz.js.map